import React, { FC, useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import Header from "components/header/Header.container";
import PublicSiteNav from "components/publicSiteNav/PublicSiteNav";
import Footer from "components/footer/Footer";
import Loader from "components/loader/loader";
import {
  Article,
  ArticleDetail,
  isArticleNew,
} from "views/articles/articleTypes";
import { fetchArticleById } from "views/articles/fetchArticleDetail";
import { fetchRelatedArticles } from "views/articles/fetchArticles";
import useExternalLinksNewTab from "utils/useExternalLinksNewTab";
import { isAuthenticated } from "utils/authUtils";
import { BASE_URL } from "store/services/URL";
import Styles from "./ArticleDetailPage.module.scss";
import { useDateTimeFormat } from "hooks/useDateTimeFormat";
import { formatArticleDisplayDate } from "helpers/dateTimeFormat";
import SEOMeta from "components/common/SEOMeta";
import { htmlToMetaDescription } from "utils/seoText";
import { getSiteBaseUrl } from "utils/seoCanonical";

// Summernote embeds inline images with relative URLs (/media/…, /summernote/…),
// which the browser would resolve against the frontend host. Prefix them with
// the API origin so they load. (Absolute URLs are left untouched.)
const absolutizeMediaUrls = (html: string): string => {
  const origin = BASE_URL.replace(/\/+$/, "");
  return html.replace(
    /(src\s*=\s*["'])(\/(?:media|summernote)\/)/gi,
    `$1${origin}$2`
  );
};

const ArticleDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { setting: dateTimeSetting } = useDateTimeFormat();
  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [related, setRelated] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  // Open external links (embedded in the content) in a new tab. Keyed on the
  // *rendered* content (empty while loading) so the effect re-runs once the
  // content div is actually mounted — not on an intermediate unbatched render.
  useExternalLinksNewTab(contentRef, loading ? "" : article?.content);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    window.scrollTo(0, 0);

    fetchArticleById(Number(id)).then((result) => {
      if (!cancelled) {
        setArticle(result);
        setLoading(false);
      }
    });

    // Topically related articles (full-text "more like this"); the backend
    // already excludes the current article and falls back to latest.
    fetchRelatedArticles(Number(id), 4).then((articles) => {
      if (!cancelled) {
        setRelated(articles);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const goToArticle = (articleId: number) =>
    history.push(`/articles/${articleId}`);

  const articleDescription = article
    ? htmlToMetaDescription(article.content || "") ||
      "Read this article on Moderate Politics."
    : undefined;
  const articleImage = article?.heroImage
    ? article.heroImage.startsWith("http")
      ? article.heroImage
      : `${getSiteBaseUrl()}${article.heroImage}`
    : undefined;

  return (
    <div className="pageShell">
      <SEOMeta
        title={article?.title}
        description={articleDescription}
        ogType="article"
        ogImage={articleImage}
        noIndex={!loading && !article}
      />
      {isAuthenticated() ? <Header /> : <PublicSiteNav />}
      <div className="pageShellMain">
        <div className="custom-container">
          {loading && (
            <div className={Styles.stateBlock}>
              <Loader />
            </div>
          )}

          {!loading && !article && (
            <p className={Styles.stateBlock}>Article not found.</p>
          )}

          {!loading && article && (
            <article className={Styles.article}>
              <header className={Styles.titleBlock}>
                <h1 className={Styles.title}>{article.title}</h1>
                <p className={Styles.meta}>
                  {formatArticleDisplayDate(article, dateTimeSetting)}
                  &nbsp;&bull;&nbsp;{article.readTime}
                </p>
              </header>

              <div className={Styles.heroImage}>
                {article.heroImage && <img src={article.heroImage} alt="" />}
              </div>

              {/* Body is admin-authored HTML (Summernote); styled via .body element rules. */}
              <div
                ref={contentRef}
                className={Styles.body}
                dangerouslySetInnerHTML={{
                  __html: absolutizeMediaUrls(article.content || ""),
                }}
              />

              <section className={Styles.related}>
                <h2 className={Styles.relatedHeading}>Related Articles</h2>
                {related.length > 0 ? (
                  <div className={Styles.relatedGrid}>
                    {related.map((r) => (
                      <div
                        key={r.id}
                        className={`${Styles.card} ${
                          r.isRead ? "" : Styles.unread
                        }`}
                        onClick={() => goToArticle(r.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            goToArticle(r.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Read article: ${r.title}`}
                      >
                        {isArticleNew(r) && (
                          <span className={Styles.newRibbon}>NEW</span>
                        )}
                        <div className={Styles.cardImage}>
                          {r.heroImage && <img src={r.heroImage} alt="" />}
                        </div>
                        <div className={Styles.cardBody}>
                          <p className={Styles.cardTitle}>{r.title}</p>
                          <p className={Styles.cardMeta}>
                            {formatArticleDisplayDate(r, dateTimeSetting)}
                            &nbsp;&bull;&nbsp;{r.readTime}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={Styles.relatedEmpty}>
                    No related articles yet.
                  </p>
                )}
              </section>
            </article>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ArticleDetailPage;
